import type { Locale } from './locales';
import { localized } from './translations';

export const chatThemeMessages = {
  sectionLabel: ['대화창 퍼즐', 'Chat puzzles'],
  sectionIntro: [
    '짧은 대화부터 장문까지 골라보세요. 어떤 퍼즐부터 시작해도 괜찮아요.',
    'Start with a short chat or try a longer one. Play in any order.',
  ],
  heading: ['대화 속 넴', 'Nem in the chat'],
  status: ['친구와 주고받는 넵', 'A chat full of Nep'],
  friend: ['친구', 'Friend'],
  you: ['나', 'You'],
  friendAvatar: ['친', 'F'],
  youAvatar: ['나', 'Y'],
  footer: ['윗획 두 개가 없는 넴을 찾아요', 'Find Nem without the two tips'],
  totalMessages: ['메시지 {n}개가 쌓였어요', '{n} messages so far'],
  recentMessages: ['최근 메시지 {n}개에서 넴을 찾아요', 'Find Nem in the latest {n} messages'],
  searchRange: ['메시지 {from}–{to}에서 찾아요', 'Search messages {from}–{to}'],
  recipe: ['{letters}글자 · 목표 {goal}개', '{letters} letters · {goal} finds'],
} satisfies Record<string, readonly [string, string]>;

export type ChatThemeMessage = keyof typeof chatThemeMessages;

export function chatThemeText(locale: Locale) {
  const text = (key: ChatThemeMessage) => {
    const [ko, en] = chatThemeMessages[key];
    return localized(locale, ko, en, (copy) => copy.chatTheme[key]);
  };
  return {
    sectionLabel: text('sectionLabel'),
    sectionIntro: text('sectionIntro'),
    heading: text('heading'),
    status: text('status'),
    friend: text('friend'),
    you: text('you'),
    friendAvatar: text('friendAvatar'),
    youAvatar: text('youAvatar'),
    footer: text('footer'),
    totalMessages: text('totalMessages'),
    recentMessages: text('recentMessages'),
    searchRange: text('searchRange'),
    recipe: text('recipe'),
  };
}

export const chatPuzzleMessages = [
  [
    ['첫 답장', 'First reply'],
    ['짧은 대화에서 넴을 세 번 찾아요.', 'Find Nem three times in a short chat.'],
  ],
  [
    ['길어진 대화', 'A longer chat'],
    ['여러 줄로 이어진 긴 답장도 꼼꼼히 살펴봐요.', 'Search every line of the longer replies.'],
  ],
  [
    ['뒤섞인 답장', 'Mixed-up replies'],
    ['정답을 찾으면 글자들이 자리를 바꿔요.', 'The letters swap places after each find.'],
  ],
  [
    ['밀려온 넵', 'Replies on the move'],
    ['답장이 밀려도 넴을 놓치지 마세요.', 'Keep finding Nem as the replies shift.'],
  ],
  [
    ['둘이 보낸 넴', 'Two hidden Nems'],
    ['넴 두 개가 숨은 대화를 여섯 번 풀어봐요.', 'Make six finds in a chat with two hidden Nems.'],
  ],
  [
    ['이어지는 답장', 'Keep the replies coming'],
    [
      '맞힐 때마다 새 답장이 와요. 최근 메시지 세 개에서 넴을 찾아요.',
      'Each find brings a new reply. Search the latest three messages for Nem.',
    ],
  ],
  [
    ['끝없는 수다', 'A lively conversation'],
    [
      '메시지 네 개를 살피며 열 번 답장해요. 새 메시지에만 넴이 있는 건 아니에요.',
      'Make ten finds across the latest four messages. Nem can hide in any of them.',
    ],
  ],
  [
    ['장문 폭탄', 'Wall of Nep'],
    [
      '48글자 장문 사이에서 넴을 찾아 답장을 이어가요.',
      'Keep replying as Nem hides among messages up to 48 letters long.',
    ],
  ],
  [
    ['답장 기다리기', 'Waiting for a reply'],
    ['친구의 긴 이야기 뒤에 짧은 답장이 와요.', 'A long story meets a short reply.'],
  ],
  [
    ['한마디씩', 'A word at a time'],
    ['긴 답장과 짧은 답장이 번갈아 이어져요.', 'Long and short replies take turns.'],
  ],
  [
    ['할 말이 많아', 'So much to say'],
    ['8글자에서 48글자까지 길이가 달라져요.', 'Replies range from 8 to 48 letters.'],
  ],
  [
    ['짧게 답해줘', 'Keep it short'],
    ['긴 메시지 하나와 짧은 답장 둘을 살펴봐요.', 'Search one long message and two short replies.'],
  ],
  [
    ['서로의 이야기', 'Two stories'],
    [
      '같은 사람이 연달아 보내도 넴은 어디에나 숨어요.',
      'Consecutive messages can hide Nem on either side.',
    ],
  ],
  [
    ['혼자 떠들기', 'A one-sided chat'],
    ['한쪽에서 쏟아지는 메시지 속 넴을 찾아요.', 'Find Nem among messages all from one sender.'],
  ],
  [
    ['긴 말 짧은 말', 'Long story, short reply'],
    ['48글자와 6글자 사이를 오가며 찾아요.', 'Switch your search between 48 and 6 letters.'],
  ],
  [
    ['대화의 마지막', 'One more reply'],
    [
      '서로 다른 네 메시지를 살피며 열여덟 번 찾아요.',
      'Make eighteen finds across four different messages.',
    ],
  ],
] as const;

export function chatPuzzleText(locale: Locale, number: number): { title: string; description: string } | undefined {
  const copy = chatPuzzleMessages[number - 1];
  if (!Number.isInteger(number) || !copy) {
    return;
  }
  const [title, description] = localized<readonly [string, string]>(
    locale,
    [copy[0][0], copy[1][0]],
    [copy[0][1], copy[1][1]],
    (catalog) => catalog.chatPuzzles[number - 1] ?? [copy[0][1], copy[1][1]],
  );
  return { title, description };
}
